import { useState, useEffect, useMemo } from 'react';
import type { ServicePlan } from '@app/types';
import { Banner, DatePicker, Field, Input, Modal, ModalActions, Select, Textarea } from '@app/components/ui';
import { SERVICE_TIMES, timeLabel } from '../../utils/serviceTimes';

interface EditServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; date: string; time: string; notes: string }) => void;
  plan: ServicePlan | null;
  /** Optional: open the Reschedule dialog from inside the Edit modal. */
  onRequestReschedule?: () => void;
}

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const EditServiceModal: React.FC<EditServiceModalProps> = ({ isOpen, onClose, onSubmit, plan, onRequestReschedule }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (plan && isOpen) {
      setTitle(plan.title);
      const d = new Date(plan.date);
      setDate(d);
      setTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
      setNotes(plan.notes || '');
    }
  }, [plan, isOpen]);

  const isPastPlan = useMemo(() => (plan ? new Date(plan.date).getTime() < Date.now() : false), [plan]);

  // The plan's own time stays selectable even when it isn't on the half-hour grid.
  const timeOptions = useMemo(() => {
    const opts = SERVICE_TIMES.map((t) => ({ value: t, label: timeLabel(t) }));
    if (time && !SERVICE_TIMES.includes(time)) opts.unshift({ value: time, label: timeLabel(time) });
    return opts;
  }, [time]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !title.trim()) return;
    onSubmit({ title: title.trim(), date: ymd(date), time, notes });
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="Edit service">
      {isPastPlan && (
        <Banner tone="amb" className="mb-4">
          <p className="font-semibold">This service has already happened.</p>
          <p className="text-t2">
            The date can&apos;t be changed on past services. To move it to a future date,{' '}
            {onRequestReschedule ? (
              <button type="button" onClick={onRequestReschedule} className="underline font-semibold">
                use Reschedule
              </button>
            ) : (
              <span className="font-semibold">use the Reschedule action</span>
            )}{' '}
            to create a new plan.
          </p>
        </Banner>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="Service name" htmlFor="edit-svc-title">
          <Input id="edit-svc-title" value={title} onChange={(e) => setTitle(e.target.value)} required disabled={isPastPlan} />
        </Field>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' }}>
          <Field label="Date" htmlFor="edit-svc-date">
            <DatePicker id="edit-svc-date" value={date} onChange={setDate} disabled={isPastPlan} />
          </Field>
          <Field label="Start time" htmlFor="edit-svc-time">
            <Select id="edit-svc-time" value={time} onChange={setTime} options={timeOptions} disabled={isPastPlan} />
          </Field>
        </div>
        <Field label="Notes for the team" optional htmlFor="edit-svc-notes">
          <Textarea
            id="edit-svc-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Call time 7:30 AM. Communion this week."
            disabled={isPastPlan}
          />
        </Field>
        {isPastPlan ? (
          <ModalActions onCancel={onClose} cancelLabel="Close" confirmLabel="Save changes" disabled />
        ) : (
          <ModalActions onCancel={onClose} confirmLabel="Save changes" disabled={!date || !title.trim()} />
        )}
      </form>
    </Modal>
  );
};
